import {FC, createContext, useContext, useState, useEffect} from 'react'
import {DefaultLayoutConfig} from './DefaultLayoutConfig'
import {
  getEmptyCssClasses,
  getEmptyCSSVariables,
  getEmptyHTMLAttributes,
  LayoutSetup,
} from './LayoutSetup'
import {
  ILayout,
  ILayoutCSSVariables,
  ILayoutCSSClasses,
  ILayoutHTMLAttributes,
} from './LayoutModels'
import {WithChildren} from '../../helpers'

export interface LayoutContextModel {
  config: ILayout
  classes: ILayoutCSSClasses
  attributes: ILayoutHTMLAttributes
  cssVariables: ILayoutCSSVariables
  setLayout: (config: LayoutSetup) => void
}

const LayoutContext = createContext<LayoutContextModel>({
  config: DefaultLayoutConfig,
  classes: getEmptyCssClasses(),
  attributes: getEmptyHTMLAttributes(),
  cssVariables: getEmptyCSSVariables(),
  setLayout: (config: LayoutSetup) => {},
})

type SubjectContextType = {
  selectedSubject: Number
  setSelectedSubject: React.Dispatch<React.SetStateAction<number>>
}

const SubjectContext = createContext<SubjectContextType>({
  selectedSubject: 0,
  setSelectedSubject: () => {},
})

type SenseMinerContextType = {
  selectedSenseMiner: Boolean
  setSelectedSenseMiner: React.Dispatch<React.SetStateAction<boolean>>
}

const SenseMinerContext = createContext<SenseMinerContextType>({
  selectedSenseMiner: false,
  setSelectedSenseMiner: () => {},
})

type SubjectPathListContextType = {
  SubjectPathList: Array<string>
  setSubjectPathList: React.Dispatch<React.SetStateAction<Array<string>>>
}

const SubjectPathListContext = createContext<SubjectPathListContextType>({
  SubjectPathList: [],
  setSubjectPathList: () => {},
})

type SelectDeviceContextType = {
  // SelectDeviceId: number | undefined
  SelectDeviceId: Array<number>
  // setSelectDeviceId: React.Dispatch<React.SetStateAction<number | undefined>>
  setSelectDeviceId: React.Dispatch<React.SetStateAction<Array<number>>>
}

const SelectDeviceContext = createContext<SelectDeviceContextType>({
  SelectDeviceId: [],
  setSelectDeviceId: () => {},
})

type SelectDeviceContextType2 = {
  // SelectDeviceId: number | undefined
  SelectDeviceId2: Array<number>
  // setSelectDeviceId: React.Dispatch<React.SetStateAction<number | undefined>>
  setSelectDeviceId2: React.Dispatch<React.SetStateAction<Array<number>>>
}

const SelectDeviceContext2 = createContext<SelectDeviceContextType2>({
  SelectDeviceId2: [],
  setSelectDeviceId2: () => {},
})

type SelectInputSelectionValueContextType = {
  SelectInputSectionValue: number[][]
  setSelectInputSectionValue: React.Dispatch<React.SetStateAction<number[][]>>
}

const SelectInputSelectionValueContext = createContext<SelectInputSelectionValueContextType>({
  SelectInputSectionValue: [],
  setSelectInputSectionValue: () => {},
})

type SelectInputSelectionValueTypeContextType = {
  SelectInputSectionValueType: number[]
  setSelectInputSectionValueType: React.Dispatch<React.SetStateAction<number[]>>
}

const SelectInputSelectionValueTypeContext =
  createContext<SelectInputSelectionValueTypeContextType>({
    SelectInputSectionValueType: [],
    setSelectInputSectionValueType: () => {},
  })

type ExtraSelectInputSelectionValueContextType = {
  ExtraSelectInputSectionValue: {id: any; controlArray: any[][]}[]
  setExtraSelectInputSectionValue: React.Dispatch<
    React.SetStateAction<{id: any; controlArray: any[][]}[]>
  >
}

const ExtraSelectInputSelectionValueContext =
  createContext<ExtraSelectInputSelectionValueContextType>({
    ExtraSelectInputSectionValue: [],
    setExtraSelectInputSectionValue: () => {},
  })

type HeaderBreadcrumbContextType = {
  changeTitle: string
  changeBeforeTitle: string
  returnPath: string
  setChangeTitle: React.Dispatch<React.SetStateAction<string>>
  setChangeBeforeTitle: React.Dispatch<React.SetStateAction<string>>
  setReturnPath: React.Dispatch<React.SetStateAction<string>>
}

const HeaderBreadcrumbContext = createContext<HeaderBreadcrumbContextType>({
  changeTitle: '',
  changeBeforeTitle: '',
  returnPath: '',
  setChangeTitle: () => {},
  setChangeBeforeTitle: () => {},
  setReturnPath: () => {},
})

const enableSplashScreen = () => {
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'flex')
  }
}

const disableSplashScreen = () => {
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'none')
  }
}

const LayoutProvider: FC<WithChildren> = ({children}) => {
  const [config, setConfig] = useState(LayoutSetup.config)
  const [classes, setClasses] = useState(LayoutSetup.classes)
  const [attributes, setAttributes] = useState(LayoutSetup.attributes)
  const [cssVariables, setCSSVariables] = useState(LayoutSetup.cssVariables)
  const [selectedSubject, setSelectedSubject] = useState<number>(0)
  const [selectedSenseMiner, setSelectedSenseMiner] = useState<boolean>(false)
  const [SubjectPathList, setSubjectPathList] = useState<Array<string>>([])
  const [SelectDeviceId, setSelectDeviceId] = useState<number[]>([])
  const [SelectDeviceId2, setSelectDeviceId2] = useState<number[]>([])
  const [SelectInputSectionValue, setSelectInputSectionValue] = useState<number[][]>([])
  const [SelectInputSectionValueType, setSelectInputSectionValueType] = useState<number[]>([])
  const [ExtraSelectInputSectionValue, setExtraSelectInputSectionValue] = useState<
    {id: any; controlArray: any[][]}[]
  >([])
  const [changeTitle, setChangeTitle] = useState<string>('')
  const [changeBeforeTitle, setChangeBeforeTitle] = useState<string>('')
  const [returnPath, setReturnPath] = useState<string>('')

  const setLayout = (_themeConfig: Partial<ILayout>) => {
    enableSplashScreen()
    const bodyClasses = Array.from(document.body.classList)
    bodyClasses.forEach((cl) => document.body.classList.remove(cl))
    LayoutSetup.updatePartialConfig(_themeConfig)
    setConfig(Object.assign({}, LayoutSetup.config))
    setClasses(LayoutSetup.classes)
    setAttributes(LayoutSetup.attributes)
    setCSSVariables(LayoutSetup.cssVariables)
    setTimeout(() => {
      disableSplashScreen()
    }, 500)
  }
  const value: LayoutContextModel = {
    config,
    classes,
    attributes,
    cssVariables,
    setLayout,
  }

  useEffect(() => {
    disableSplashScreen()
  }, [])

  return (
    <LayoutContext.Provider value={value}>
      <SubjectContext.Provider value={{selectedSubject, setSelectedSubject}}>
        <HeaderBreadcrumbContext.Provider
          value={{
            changeTitle,
            changeBeforeTitle,
            returnPath,
            setChangeTitle,
            setChangeBeforeTitle,
            setReturnPath,
          }}
        >
          <SenseMinerContext.Provider value={{selectedSenseMiner, setSelectedSenseMiner}}>
            <SubjectPathListContext.Provider value={{SubjectPathList, setSubjectPathList}}>
              <SelectDeviceContext.Provider value={{SelectDeviceId, setSelectDeviceId}}>
                <SelectDeviceContext2.Provider value={{SelectDeviceId2, setSelectDeviceId2}}>
                  <SelectInputSelectionValueContext.Provider
                    value={{SelectInputSectionValue, setSelectInputSectionValue}}
                  >
                    <SelectInputSelectionValueTypeContext.Provider
                      value={{SelectInputSectionValueType, setSelectInputSectionValueType}}
                    >
                      <ExtraSelectInputSelectionValueContext.Provider
                        value={{ExtraSelectInputSectionValue, setExtraSelectInputSectionValue}}
                      >
                        {children}
                      </ExtraSelectInputSelectionValueContext.Provider>
                    </SelectInputSelectionValueTypeContext.Provider>
                  </SelectInputSelectionValueContext.Provider>
                </SelectDeviceContext2.Provider>
              </SelectDeviceContext.Provider>
            </SubjectPathListContext.Provider>
          </SenseMinerContext.Provider>
        </HeaderBreadcrumbContext.Provider>
      </SubjectContext.Provider>
    </LayoutContext.Provider>
  )
}

export {LayoutContext, LayoutProvider}

export function useLayout() {
  return useContext(LayoutContext)
}

export const useSelectInputSectionValue = () => useContext(SelectInputSelectionValueContext)
export const useSelectInputSectionValueType = () => useContext(SelectInputSelectionValueTypeContext)

export const useExtraSelectInputSectionValue = () =>
  useContext(ExtraSelectInputSelectionValueContext)

export const useSelectDeviceId = () => useContext(SelectDeviceContext)
export const useSelectDeviceId2 = () => useContext(SelectDeviceContext2)

export const useSubjectPathList = () => useContext(SubjectPathListContext)

export const useSenseMinerContext = () => useContext(SenseMinerContext)

export const useSubjectContext = () => useContext(SubjectContext)

export const useHeaderBreadcrumb = () => useContext(HeaderBreadcrumbContext)
